// MarkdownComponents.js - Simplified version using theme global styles
import React from 'react';
import { Box, Code } from '@chakra-ui/react';
import CodeBlock from './CodeBlock';

// Custom hook that returns markdown components
const useMarkdownComponents = () => {
    return {

        code: ({ inline, className, children, node, ...props }) => {
            const match = /language-(\w+)/.exec(className || "");

            if (!inline && match) {
                return (
                    <CodeBlock
                        node={node}
                        inline={inline}
                        className={className}
                        {...props}
                    >
                        {children}
                    </CodeBlock>
                );
            }

            // Always use inline variant for non-block code
            return (
                <Code variant="inline" {...props}>
                    {children}
                </Code>
            );
        },

        // Pre-formatted code
        pre: ({ children }) => (
            <Box
                as="pre"
                bg="transparent"
                border="none"
                py={0}
                my={3}
                overflowX="auto"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
            >
                {children}
            </Box>
        ),

        // All other elements use native HTML - theme handles styling
        p: ({ children }) => (
        <Box as="p" mt={2} mb={3}>
            {children}
        </Box>
        ),
        h1: ({ children }) => <h1>{children}</h1>,
        h2: ({ children }) => <h2>{children}</h2>,
        h3: ({ children }) => <h3>{children}</h3>,
        h4: ({ children }) => <h4>{children}</h4>,
        h5: ({ children }) => <h5>{children}</h5>,
        h6: ({ children }) => <h6>{children}</h6>,
        em: ({ children }) => <em>{children}</em>,
        strong: ({ children }) => <strong>{children}</strong>,
        ul: ({ children }) => <ul>{children}</ul>,
        ol: ({ children }) => <ol>{children}</ol>,
        li: ({ children }) => <li>{children}</li>,
        blockquote: ({ children }) => <blockquote>{children}</blockquote>,
        hr: () => <hr />,
        a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
            </a>
        ),

        // Wrap tables in a scroll container so wide tables don't break
        // the mobile layout. The inner table keeps its theme styling.
        table: ({ children }) => (
            <Box
                overflowX="auto"
                maxW="100%"
                minW={0}
                my={4}
                sx={{
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehaviorX: 'contain',
                }}
            >
                <table>{children}</table>
            </Box>
        ),

        thead: ({ children }) => <thead>{children}</thead>,
        tbody: ({ children }) => <tbody>{children}</tbody>,
        tr: ({ children }) => <tr>{children}</tr>,
        th: ({ children, align }) => <th style={{ textAlign: align || "left" }}>{children}</th>,
        td: ({ children, align }) => <td style={{ textAlign: align || "left" }}>{children}</td>,
        img: ({ src, alt }) => <img src={src} alt={alt} />,
        
        // Task lists (checkboxes)
        input: ({ type, checked, disabled }) => {
            if (type === 'checkbox') {
                return (
                    <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled || true}
                        readOnly
                    />
                );
            }
            return null;
        },
    };
};

export default useMarkdownComponents;